import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import MessagingFolder from '../../model/messagingFolder.model';
import MessagingAccount from '../../model/messagingAccount.model';
import MessagingMessage from '../../model/messagingMessage.model';
import {environment} from '../../environments/environment';
import {BehaviorSubject, Observable, of, skipWhile, throwError} from 'rxjs';
import MessagingThread from '../../model/messagingThread.model';
import {catchError, switchMap} from 'rxjs/operators';
import Pagination from "../../model/pagination";
import MessagingPagination from "../../model/messagingPagination.model";
import NewMessagingMessage from "../../model/newMessagingMessage.model";
import Crypto from "../../helper/crypto";
import {cache} from "../../cache";

@Injectable()
export default class MessagingMessageService {
    public constructor(private http: HttpClient) {
    }

    public globalList(query?: string, page?: number, limit?: number, mode?: 'inbox'|'outbox', useCache?: boolean) {
        let params = {
            page: page || 1,
            limit: limit || 20,
            mode: mode || 'inbox'
        };
        if(query) {
            params['query'] = query;
        }
        let queryArr = [];
        for(let key of Object.keys(params)) {
            queryArr.push(key + '=' + params[key]);
        }



        const url = environment.apiUrl + '/messaging/messages/global?' +   queryArr.join('&');

        const key = 'cache-' + Crypto.SHA1(url);
        const subject = new BehaviorSubject<MessagingPagination<MessagingMessage>>(null);

        if(page == 1) {
            cache.getCacheItem(key).then(result => {
                if(result) {
                    subject.next(JSON.parse(result.result));
                }
            });
        }

        this.http.get<MessagingPagination<MessagingMessage>>(url).subscribe(result => {
            subject.next(result);
            cache.removeCacheItem(key).then(_ => {
                cache.insertCacheItem({
                    id: key,
                    result: JSON.stringify(result)
                });
            });
        });

        return subject.pipe(skipWhile(e => {
            return e == null
        }));
    }

    public list(account: MessagingAccount, folder: MessagingFolder, query?: string, page?: number, limit?: number): Observable<MessagingPagination<MessagingMessage>> {
        let params = {
            page: page || 1,
            limit: limit || 20
        };
        if(account) {
            params['account'] = account.id;
        }
        if(query) {
            params['query'] = query;
        }
        if(folder) {
            params['folder'] = folder.realName;
        }

        let queryArr = [];
        for(let key of Object.keys(params)) {
            queryArr.push(key + '=' + params[key]);
        }
        const url = environment.apiUrl + '/messaging/messages?' +   queryArr.join('&');
        const timestamp = Date.now();

        const key = 'cache-' + Crypto.SHA1(url);
        const subject = new BehaviorSubject<MessagingPagination<MessagingMessage>>(null);

        const cachedResult = localStorage.getItem(key);

        if(page == 1) {
            cache.getCacheItem(key).then(result => {
                if(result) {
                    let data = JSON.parse(result.result);
                    data['cachedVersion'] = true;
                    subject.next(data);
                }
            });
        }

        this.http.get<MessagingPagination<MessagingMessage>>(url).subscribe(result => {
            subject.next(result);
            cache.removeCacheItem(key).then(_ => {
                cache.insertCacheItem({
                    id: key,
                    result: JSON.stringify(result)
                });
            });
        });

        return subject.pipe(skipWhile(e => {
            return e == null
        }));
    }

    public show(id: string, account: MessagingAccount, useCache?: boolean): Observable<MessagingMessage> {
        useCache = useCache || false;
        const url = environment.apiUrl + '/messaging/messages/' + id + '?account=' + account.id;

        if(useCache) {

        }

        return this.http.get<MessagingMessage>(url).pipe(switchMap(result => {
            const timestamp = Date.now();
            return of(result);
        }));
    }

    public content(id: string, account: MessagingAccount, useCache?: boolean): Observable<MessagingMessage> {
        useCache = useCache || false;
        const url = environment.apiUrl + '/messaging/messages/' + id + '/content?account=' + account.id;

        if(useCache) {

        }

        return this.http.get<MessagingMessage>(url).pipe(switchMap(result => {
            const timestamp = Date.now();
            return of(result);
        }));
    }

    public thread(account: MessagingAccount|string, messageId: string, useCache?: boolean): Observable<MessagingThread> {
        useCache = useCache || false;
        let accountId = typeof account == 'string' ? account : account.id;

        const url = environment.apiUrl + '/messaging/messages/' + messageId + '/thread?account=' + accountId;
        const key = 'cache-' + Crypto.SHA1(url);
        const subject = new BehaviorSubject<MessagingThread>(null);
        if(useCache) {
            cache.getCacheItem(key).then(result => {
                if(result) {
                    subject.next(JSON.parse(result.result));
                }
            });
        }

        this.http.get<MessagingThread>(url).subscribe(result => {
            subject.next(result);
            cache.removeCacheItem(key).then(_ => {
                cache.insertCacheItem({
                    id: key,
                    result: JSON.stringify(result)
                });
            });
        });

        return subject.pipe(skipWhile(e => {
            return e == null
        }));
    }

    public send(message: NewMessagingMessage, account: MessagingAccount, thread?: MessagingThread): Observable<MessagingMessage> {
        return this.http.post<MessagingMessage>(environment.apiUrl + '/messaging/messages?account=' + account.id
            + (thread ? '&thread=' + thread.id : ''), message);
    }

    public delete(message: MessagingMessage, account: MessagingAccount): Observable<{ deleted: boolean, moved: { destination: string, message: string } }> {
        return this.http.delete<{ deleted: boolean, moved: { destination: string, message: string } }>(environment.apiUrl + '/messaging/messages/' + message.id + '?account=' + account.id);
    }

    public moveMessage(message: MessagingMessage, folder: MessagingFolder, account: MessagingAccount): Observable<{ path: string, id: string, uid: number }> {
        return this.http.put<{ path: string, id: string, uid: number }>(environment.apiUrl + '/messaging/messages/' + message.id + '/move?account=' + account.id, {
            path: folder.realName
        });
    }

    public addFlag(message: MessagingMessage, account: MessagingAccount, flag: string): Observable<MessagingMessage> {
        return this.http.put<any>(environment.apiUrl + '/messaging/messages/' + message.id + '/add-flag?account=' + account.id, {
            flag: flag
        });
    }

    public removeFlag(message: MessagingMessage, account: MessagingAccount, flag: string): Observable<MessagingMessage> {
        return this.http.put<any>(environment.apiUrl + '/messaging/messages/' + message.id + '/remove-flag?account=' + account.id, {
            flag: flag
        });
    }
}
